import { documentService } from "@/app/providers/services";
import { EmptyContent } from "@/common";
import { SourceEditor, createSourcePlugins } from "@/common/editor/RuleAuthor/SourceEditor";
import { useFullRelease } from "@/common/hooks_useFullRelease";
import { trimDocName } from "@/hooks/documents";
import { AttributeUsage } from "@/pages/models/release/DataModel/route/Attributes/DocumentUsage";
import styled from "@emotion/styled";
import AttributeExplanation from "@imminently/decisively-ui/AttributeExplanation";
import LoadingSpinner from "@imminently/decisively-ui/LoadingSpinner";
import RadioButtons from "@imminently/decisively-ui/RadioButtons";
import Tabs from "@imminently/decisively-ui/Tabs";
import { Typography } from "@mui/material";
import type { RuleDocContentsV3 } from "@packages/commons";
import { PlateProvider } from "@udecode/plate-core";
import get from "lodash/get";
import { useState } from "react";
import { useTranslation } from "react-i18next";

const AttributeFullExplanation = ({ attr }) => {
  const { t } = useTranslation();

  if (!attr) return null;

  return (
    <Tabs
      defaultValue="rules"
      className="flex flex-col h-full"
    >
      <Tabs.List>
        <Tabs.Trigger value="rules">Rules</Tabs.Trigger>
        {attr.definedIn && <Tabs.Trigger value="source">Source</Tabs.Trigger>}
        <Tabs.Trigger value="usage">Usage</Tabs.Trigger>
      </Tabs.List>
      <Tabs.Content
        value="rules"
        className="flex-1 overflow-auto p-2"
      >
        <AttributeExplanation
          node={attr}
          selectedNode={attr}
        />
      </Tabs.Content>
      {attr.definedIn && (
        <Tabs.Content
          value="source"
          className="flex-1 overflow-auto p-2"
        >
          <AttributeSource node={attr} />
        </Tabs.Content>
      )}
      <Tabs.Content
        value="usage"
        className="flex-1 overflow-auto p-2"
      >
        <AttributeUsage node={attr} />
      </Tabs.Content>
    </Tabs>
  );
};

const EmptyAttributeSourceWrap = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100%;
`;
const ATTRIBUTE_SOURCE_ID = "attribute-source";
type SourceViewMode = "section" | "full";

const getSectionSourceForNode = (source: any, sectionId?: unknown) => {
  if (!Array.isArray(source)) return null;
  if (typeof sectionId !== "string" || sectionId === "" || sectionId === "global") return null;

  const section = source.find((item) => item?.type === "section" && item?.id === sectionId);
  return Array.isArray(section?.children) ? section.children : null;
};

const AttributeSource = ({ node }) => {
  const release = useFullRelease();
  const { t } = useTranslation();
  const [sourceViewMode, setSourceViewMode] = useState<SourceViewMode>("section");

  // Find the document that this is defined in
  if (!node || !node.definedIn) return <p>Invalid node</p>;
  if (!release) return null;
  const path = trimDocName(String(node.definedIn)).split("/");
  path.shift();
  const info = get(release.documents, path);

  if (!info) {
    console.error("Document not found", { definedIn: node.definedIn, path, documents: release.documents });
    return (
      <EmptyContent
        key="notfound"
        messages={["Document not found", `Failed to load ${node.definedIn}`]}
      />
    );
  }

  const { data, isLoading, isError } = documentService.useGetOne(info.reference, {});

  if (isLoading) {
    return (
      <EmptyContent
        key="loading"
        messages={[
          "Loading source",
          <LoadingSpinner
            key="loading-source-spinner"
            size="sm"
          />,
        ]}
      />
    );
  }

  if (!data || isError) {
    return (
      <EmptyContent
        key="error"
        messages={["Error getting document"]}
      />
    );
  }

  if (data.version === 1) {
    return (
      <EmptyContent
        key="unsupported"
        messages={["Unsupported document version 1"]}
      />
    );
  }

  const contents = data.contents as RuleDocContentsV3;
  const sectionSource = getSectionSourceForNode(contents.source, node.sectionId);
  const canToggleSourceView = sectionSource !== null;
  const source = sourceViewMode === "full" || !sectionSource ? contents.source : sectionSource;

  const isSourceEmpty = (() => {
    if (Array.isArray(source) === false || source.length !== 1) return false;

    const first = source[0];
    if (
      typeof first !== "object" ||
      first === null ||
      !("type" in first) ||
      first.type !== "p" ||
      !("children" in first) ||
      Array.isArray(first.children) === false ||
      first.children.length !== 1
    )
      return false;

    const firstChild = first.children[0];
    if (typeof firstChild !== "object" || firstChild === null || !("text" in firstChild) || firstChild.text !== "") {
      return false;
    }

    return true;
  })();

  return (
    <PlateProvider
      key={`${ATTRIBUTE_SOURCE_ID}-${sourceViewMode}-${node.definedIn}-${node.sectionId ?? "no-section"}`}
      id={ATTRIBUTE_SOURCE_ID}
      plugins={createSourcePlugins()}
      initialValue={source}
    >
      {canToggleSourceView ? (
        <div className="flex justify-end mb-2">
          <RadioButtons
            name="source_view"
            value={sourceViewMode}
            onValueChange={(value) => setSourceViewMode(value as SourceViewMode)}
            options={[
              { label: "Section", value: "section" },
              { label: "Full source", value: "full" },
            ]}
          />
        </div>
      ) : null}

      {isSourceEmpty ? (
        <EmptyAttributeSourceWrap>
          <Typography>{t("sessions.source_section_is_blank")}</Typography>
        </EmptyAttributeSourceWrap>
      ) : (
        <SourceEditor
          id={ATTRIBUTE_SOURCE_ID}
          editableProps={{ readOnly: true }}
        />
      )}
    </PlateProvider>
  );
};

export default AttributeFullExplanation;
